import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { useUnifiedStore } from "../../store/StoreContext";
import { useTranslation } from "../../hooks";
import { getCurrencySymbol } from "../../constants";
import { followUpFitsSlot, selectedPatientFollowUp, } from "../../store/slices/followUpSlice";
export function FollowUpOffer({ regularVisit }) {
    const { t } = useTranslation("appointment");
    const followUpEligibility = useUnifiedStore((s) => s.followUpEligibility);
    const followUpSelected = useUnifiedStore((s) => s.followUpSelected);
    const followUpGrid = useUnifiedStore((s) => s.followUpGrid);
    const patientTab = useUnifiedStore((s) => s.patientTab);
    const selectedPatient = useUnifiedStore((s) => s.selectedPatient);
    const selectedPack = useUnifiedStore((s) => s.selectedPack);
    const selectedActiveSessionPack = useUnifiedStore((s) => s.selectedActiveSessionPack);
    const activeSystemType = useUnifiedStore((s) => s.activeSystemType);
    const selectedSlot = useUnifiedStore((s) => s.selectedSlot);
    const setFollowUpSelected = useUnifiedStore((s) => s.setFollowUpSelected);
    const chooseFollowUpSlot = useUnifiedStore((s) => s.chooseFollowUpSlot);
    const leaveFollowUpGrid = useUnifiedStore((s) => s.leaveFollowUpGrid);
    const eligibility = selectedPatientFollowUp({
        followUpEligibility,
        followUpSelected,
        patientTab,
        selectedPatient,
        selectedPack,
        selectedActiveSessionPack,
        activeSystemType,
        selectedSlot,
    });
    if (!eligibility) {
        if (!followUpGrid)
            return null;
        return (_jsx(Alert, { severity: "warning", sx: { mt: 2 }, action: _jsx(Button, { color: "inherit", size: "small", onClick: leaveFollowUpGrid, children: t("pickRegularSlot", "Pick a regular slot") }), children: t("followUpSlotNotEligible", "This time slot is for a follow-up visit, which this patient isn't eligible for.") }));
    }
    if (selectedPack || selectedActiveSessionPack)
        return null;
    const currency = eligibility.currency ?? null;
    const fee = eligibility.discountedFee;
    const feeLabel = fee == null || !currency
        ? null
        : fee === 0
            ? t("free", "Free")
            : `${getCurrencySymbol(currency)}${fee.toLocaleString()}`;
    const showRegular = fee != null &&
        !!currency &&
        regularVisit !== null &&
        regularVisit.currency === currency &&
        regularVisit.amount > fee;
    const fits = followUpFitsSlot(eligibility, selectedSlot);
    const mins = eligibility.followUpDurationMins;
    return (_jsxs(Box, { sx: {
            mt: 2,
            border: 1,
            borderColor: followUpSelected && fits ? "primary.main" : "divider",
            borderRadius: 1.5,
            px: 1.5,
            py: 1.25,
        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "flex-start", gap: 1 }, children: [fits && (_jsx(Checkbox, { checked: followUpSelected, disabled: followUpGrid, onChange: (e) => setFollowUpSelected(e.target.checked), size: "small", sx: { p: 0, mt: 0.25 }, slotProps: {
                            input: {
                                "aria-label": t("bookAsFollowUp", "Book as a follow-up visit"),
                            },
                        } })), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: fits
                                    ? t("bookAsFollowUp", "Book as a follow-up visit")
                                    : t("eligibleForFollowUp", "You're eligible for a follow-up visit") }), _jsxs(Typography, { variant: "caption", color: "text.secondary", component: "div", children: [feeLabel && (_jsxs(_Fragment, { children: [_jsx(Box, { component: "span", sx: { fontWeight: 600, color: "success.main" }, children: feeLabel }), showRegular && (_jsx(Box, { component: "span", sx: { textDecoration: "line-through", ml: 0.75 }, children: `${getCurrencySymbol(regularVisit.currency)}${regularVisit.amount.toLocaleString()}` }))] })), eligibility.validUntil && (_jsx(Box, { component: "span", sx: { ml: feeLabel ? 0.75 : 0 }, children: t("followUpValidUntil", "· valid until {{date}}", {
                                            date: eligibility.validUntil,
                                        }) }))] })] })] }), !fits && mins != null && (_jsxs(Box, { sx: { mt: 1 }, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", component: "div", children: t("followUpNeedsSlotLength", "Follow-up visits are {{minutes}} minutes long, so they need a different time slot.", { minutes: mins }) }), _jsx(Button, { variant: "outlined", size: "small", onClick: chooseFollowUpSlot, sx: { mt: 1, textTransform: "none" }, children: t("chooseFollowUpSlot", "Choose a follow-up slot") })] })), followUpGrid && (_jsx(Button, { size: "small", onClick: leaveFollowUpGrid, sx: { mt: 1, px: 0, textTransform: "none" }, children: t("bookRegularVisitInstead", "Book a regular visit instead") }))] }));
}
